import { DEFAULT_PLACEHOLDER } from 'core/constants';
// Direct sub-path to avoid the `core/elements` barrel pulling AG-Grid deps into tests.
import { Link } from 'core/elements/Link';
import { IInfoCardLine } from 'core/types';
import {
  IPartFieldValue,
  PartActiveStatus,
  PartFieldDataType,
  PartMainDetailsField,
  PartSpecificationsGroupName,
} from 'parts/types';
import { Color } from 'theme/enums';

export interface ISection {
  items: IInfoCardLine[];
  key: string;
  title: string;
}

const URL_REGEX = /^https?:\/\//i;
const MULTI_VALUE_SEPARATOR = '|#|';

export const ellipsisSx = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  width: '100%',
  minWidth: 0,
};

// Strip the legacy `|#|` join (defensive — BE may switch to a real array). Already-array
// values are joined with a comma.
export const formatFieldValue = (value: string | string[]): string => {
  if (Array.isArray(value)) {
    return value.join(', ');
  }

  return value.split(MULTI_VALUE_SEPARATOR).join(', ');
};

const isEmptyFieldValue = (value: IPartFieldValue['value']): boolean =>
  value == null || value === '' || (Array.isArray(value) && value.length === 0);

const pickPrimitiveValue = (value: string | string[]): string =>
  Array.isArray(value) ? value[0] ?? '' : value;

export const fieldToInfoCardItem = (
  field: IPartFieldValue,
  yesLabel: string,
  noLabel: string
): IInfoCardLine => {
  const label = field.name ?? '';

  if (isEmptyFieldValue(field.value) || field.value == null) {
    return { label, value: DEFAULT_PLACEHOLDER };
  }

  if (field.dataType === PartFieldDataType.BOOLEAN) {
    const raw = pickPrimitiveValue(field.value);
    if (raw === '1' || raw === 'true') {
      return { label, value: yesLabel };
    }
    if (raw === '0' || raw === 'false') {
      return { label, value: noLabel };
    }
  }

  const formatted = formatFieldValue(field.value);

  if (URL_REGEX.test(formatted)) {
    return {
      label,
      value: (
        <Link
          href={formatted}
          target="_blank"
          rel="noopener noreferrer"
          title={formatted}
          sx={ellipsisSx}
        >
          {formatted}
        </Link>
      ),
    };
  }

  return { label, value: formatted };
};

export const getStatusColor = (value: IPartFieldValue['value']): Color => {
  const raw = Array.isArray(value) ? value[0] : value;

  if (raw === PartActiveStatus.ACTIVE) {
    return Color.SUCCESS;
  }
  if (raw === PartActiveStatus.OBSOLETE) {
    return Color.DANGER;
  }

  return Color.NEUTRAL;
};

// First-wins cherry-pick by `name`. Multiple rows with the same name (rare) keep the first.
export const buildFieldsByName = (fields: IPartFieldValue[]): Map<string, IPartFieldValue> => {
  const byName = new Map<string, IPartFieldValue>();

  fields.forEach((field) => {
    if (field.name && !byName.has(field.name)) {
      byName.set(field.name, field);
    }
  });

  return byName;
};

interface IBuildSpecificationSectionsOptions {
  detailsLabel: string;
  yesLabel: string;
  noLabel: string;
}

interface IBuildSpecificationSectionsResult {
  dimensionsSection: ISection | null;
  leftColumnSections: ISection[];
  rightColumnSections: ISection[];
}

const MAX_DP = Number.MAX_SAFE_INTEGER;

// Sort key matches the legacy Specifications BE: display_position ASC (nulls last),
// then field name ASC as a stable tiebreaker.
const sortByDisplayPosition = (rows: IPartFieldValue[]): IPartFieldValue[] =>
  [...rows].sort((a, b) => {
    const dpDiff = (a.displayPosition ?? MAX_DP) - (b.displayPosition ?? MAX_DP);
    if (dpDiff !== 0) {
      return dpDiff;
    }

    return (a.name ?? '').localeCompare(b.name ?? '');
  });

const minDisplayPosition = (rows: IPartFieldValue[]): number | null => {
  let min: number | null = null;

  rows.forEach((row) => {
    if (row.displayPosition != null && (min == null || row.displayPosition < min)) {
      min = row.displayPosition;
    }
  });

  return min;
};

// Find the prefix length that produces the most even item-count split between two columns.
// Ties go to the earlier (smaller-left) split, keeping section order stable.
const findBalancedSplit = (sections: ISection[]): number => {
  const totalItems = sections.reduce((sum, section) => sum + section.items.length, 0);

  let bestSplit = sections.length;
  let bestDiff = Infinity;
  let runningSum = 0;

  for (let i = 0; i < sections.length; i++) {
    runningSum += sections[i].items.length;
    const diff = Math.abs(2 * runningSum - totalItems);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestSplit = i + 1;
    }
  }

  return bestSplit;
};

/**
 * Bucket `part_field_values` rows into the Spec tab's two side-by-side columns.
 *
 *   - Empty-group rows become a single "Details" section.
 *   - Each named group becomes its own section.
 *   - Within a section, rows sort by `display_position` ASC (nulls last), then field name ASC
 *     — matches the legacy Specifications BE sort key.
 *   - Sections are ordered globally by their min numeric `display_position` (nulls last). BE
 *     insertion order is the stable tiebreaker at the section level (`Map` preserves insertion
 *     order; `Array.sort` is stable since ES2019).
 *   - The flat ordered list is then split into two columns at the prefix length that produces
 *     the most even item-count balance.
 *   - Skips the Status field (rendered in the Main Details card).
 *   - The Dimensions group is returned separately so it can be rendered under Main Details.
 */
export const buildSpecificationSections = (
  fields: IPartFieldValue[],
  { detailsLabel, yesLabel, noLabel }: IBuildSpecificationSectionsOptions
): IBuildSpecificationSectionsResult => {
  const detailsRows: IPartFieldValue[] = [];
  const dimensionsRows: IPartFieldValue[] = [];
  const groupBuckets = new Map<string, IPartFieldValue[]>();

  fields.forEach((field) => {
    const groupKey = field.group ?? '';

    // Active/Obsolete is rendered in the Main Details card already.
    if (field.name === PartMainDetailsField.STATUS) {
      return;
    }

    if (groupKey === PartSpecificationsGroupName.DIMENSIONS) {
      dimensionsRows.push(field);

      return;
    }

    if (!groupKey) {
      detailsRows.push(field);

      return;
    }

    const existing = groupBuckets.get(groupKey) ?? [];
    existing.push(field);
    groupBuckets.set(groupKey, existing);
  });

  const buildSection = (key: string, title: string, rows: IPartFieldValue[]): ISection => ({
    key,
    title,
    items: rows.map((row) => fieldToInfoCardItem(row, yesLabel, noLabel)),
  });

  const sectionEntries: Array<{ section: ISection; minDp: number | null }> = [];

  if (detailsRows.length > 0) {
    sectionEntries.push({
      section: buildSection('details', detailsLabel, sortByDisplayPosition(detailsRows)),
      minDp: minDisplayPosition(detailsRows),
    });
  }

  groupBuckets.forEach((rows, group) => {
    sectionEntries.push({
      section: buildSection(group, group, sortByDisplayPosition(rows)),
      minDp: minDisplayPosition(rows),
    });
  });

  sectionEntries.sort((a, b) => (a.minDp ?? MAX_DP) - (b.minDp ?? MAX_DP));

  const orderedSections = sectionEntries.map((entry) => entry.section);
  const splitIndex = findBalancedSplit(orderedSections);

  const dimensionsSection: ISection | null =
    dimensionsRows.length > 0
      ? buildSection(
          PartSpecificationsGroupName.DIMENSIONS,
          PartSpecificationsGroupName.DIMENSIONS,
          sortByDisplayPosition(dimensionsRows)
        )
      : null;

  return {
    dimensionsSection,
    leftColumnSections: orderedSections.slice(0, splitIndex),
    rightColumnSections: orderedSections.slice(splitIndex),
  };
};
